import { FC, Fragment, useEffect, useState } from 'react';
import {
	Button,
	Form,
	Modal,
	Row,
	Space,
	Switch,
	Table,
	Tooltip,
	Typography,
} from 'antd';
import {
	InfoCircleOutlined,
	PlusOutlined,
	ReloadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import { useApp } from '@/App';
import CardPanel from '@/components/CardPanel';
import { FieldType, renderField } from '@/components/FormBuilder';
import formRule from '@/libs/antd/rules';
import {
	MasterMenu,
	MasterMenuPayload,
	createMasterMenu,
	getMasterMenus,
	updateMasterMenu,
} from '@/libs/fleet-api/services/master-menus';

const MASTER_MENU_FORMS = [
	{
		name: 'title',
		type: 'text',
		label: 'Title',
		config: {
			placeholder: 'Title',
		},
		rules: [formRule.required()],
	},
	{
		name: 'menuKey',
		type: 'text',
		label: 'Menu Key',
		width: '1/2',
		config: {
			placeholder: 'Menu Key',
		},
		rules: [formRule.required()],
	},
	{
		name: 'parentId',
		type: 'select',
		label: 'Menu Parent',
		width: '1/2',
		config: {
			placeholder: 'Select Menu Parent',
			allowClear: true,
			showSearch: true,
			options: [],
			filterOption: (value, option) => {
				return option?.label
					?.toLocaleString()
					.toLocaleLowerCase()
					.includes(value.toLocaleLowerCase());
			},
		},
	},
	{
		name: 'url',
		type: 'text',
		label: 'URL',
		width: '1/2',
		config: {
			placeholder: 'URL',
		},
		rules: [formRule.required()],
	},
	{
		name: 'isActive',
		type: 'switch',
		label: 'Status',
		width: '1/2',
		config: {
			checkedChildren: 'Active',
			unCheckedChildren: 'Inactive',
		},
	},
] as FieldType[];

const MasterMenuPage: FC = () => {
	const { notification } = useApp();
	const { data, isLoading, isFetching, refetch } = useQuery({
		queryKey: ['getMasterMenus'],
		queryFn: () => getMasterMenus(),
	});

	const dataSource =
		data?.data.map((value, key) => {
			return {
				...value,
				key,
			};
		}) ?? [];

	const [modalDetailForm] = Form.useForm<MasterMenuPayload>();
	const [modalDetail, setModalDetail] = useState<
		| {
				type: 'create';
		  }
		| {
				type: 'update';
				data: MasterMenu;
		  }
	>();

	useEffect(() => {
		if (modalDetail?.type !== 'update') return;
		modalDetailForm.setFieldsValue({
			...modalDetail.data,
			...(modalDetail.data.parentId?.startsWith('00000000') && {
				parentId: null,
			}),
		});
	}, [modalDetail, modalDetailForm]);

	return (
		<Fragment>
			<CardPanel
				title="Master Menu"
				extra={
					<Space size="small">
						<Button
							icon={<ReloadOutlined />}
							loading={isFetching}
							onClick={() => refetch()}
						>
							Refresh
						</Button>
						<Button
							type="primary"
							icon={<PlusOutlined />}
							onClick={() => {
								setModalDetail({ type: 'create' });
							}}
						>
							New Entry
						</Button>
					</Space>
				}
				styles={{
					header: {
						paddingRight: 12,
					},
				}}
			>
				<Table
					dataSource={dataSource}
					loading={isLoading || isFetching}
					columns={[
						{
							title: '#',
							align: 'center',
							width: 60,
							render: (_, __, index) => {
								return index + 1;
							},
						},
						{
							title: 'Title',
							render: (_, record) => (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										setModalDetail({
											type: 'update',
											data: record,
										});
									}}
								>
									{record.title}
								</a>
							),
						},
						Table.EXPAND_COLUMN,
						{
							title: 'Menu',
							dataIndex: 'menuKey',
						},
						{
							title: 'Status',
							align: 'center',
							dataIndex: 'isActive',
							render: (isActive) => (
								<Switch
									value={isActive}
									checkedChildren="Active"
									unCheckedChildren="Inactive"
									style={{ cursor: 'default' }}
								/>
							),
						},
						{
							title: 'Created',
							align: 'center',
							dataIndex: 'createdDate',
							render: (createdDate) => {
								const date = dayjs(createdDate);
								const shortDate = date.format('DD-MM-YYYY');
								const longDate = date.format('DD MMMM YYYY, HH:mm');

								return (
									<Space>
										<span>{shortDate}</span>
										<Tooltip title={longDate}>
											<InfoCircleOutlined />
										</Tooltip>
									</Space>
								);
							},
						},
						{
							title: 'Updated',
							align: 'center',
							dataIndex: 'createdDate',
							render: (createdDate) => {
								const date = dayjs(createdDate);
								const shortDate = date.format('DD-MM-YYYY');
								const longDate = date.format('DD MMMM YYYY, HH:mm');

								return (
									<Space>
										<span>{shortDate}</span>
										<Tooltip title={longDate}>
											<InfoCircleOutlined />
										</Tooltip>
									</Space>
								);
							},
						},
					]}
					expandable={{
						expandedRowRender(record) {
							return (
								<div style={{ padding: 16 }}>
									<pre style={{ margin: 0 }}>
										{JSON.stringify(record, null, 2)}
									</pre>
								</div>
							);
						},
					}}
				/>
			</CardPanel>

			<Modal
				open={Boolean(modalDetail)}
				width={500}
				title={modalDetail?.type === 'update' ? 'Edit' : 'New'}
				afterClose={() => modalDetailForm.resetFields()}
				onCancel={() => setModalDetail(undefined)}
				onOk={() => modalDetailForm.submit()}
			>
				<Form
					form={modalDetailForm}
					layout="vertical"
					onFinish={async (values) => {
						try {
							if (modalDetail?.type === 'update') {
								if (!values.parentId) values.parentId = null;
								await updateMasterMenu(modalDetail.data.id, values);
								notification.success({
									message: 'Updated',
									description: 'data was successfully updated',
								});
							} else {
								await createMasterMenu(values);
								notification.success({
									message: 'Created',
									description: 'data was successfully created',
								});
							}
							setModalDetail(undefined);
							refetch();
						} catch (error) {
							console.log(error);
						}
					}}
				>
					<Row gutter={[16, 16]}>
						{MASTER_MENU_FORMS.map((field) => {
							if (field.type === 'select' && field.name === 'parentId') {
								field.config!.options = data?.data.map((record) => {
									return {
										value: record.id,
										label: record.title,
										menuKey: record.menuKey,
										...(modalDetail?.type === 'update' && {
											disabled: modalDetail.data.id === record.id,
										}),
									};
								});
								field.config!.optionRender = (option) => (
									<div style={{ paddingBlock: 4 }}>
										<div>{option.label}</div>
										<div style={{ fontWeight: 'normal' }}>
											<Typography.Text
												type="secondary"
												style={{ display: 'block' }}
											>
												{option.value}
											</Typography.Text>
											<Typography.Text type="secondary">
												{option.data.menuKey}
											</Typography.Text>
										</div>
									</div>
								);
							}
							return field;
						}).map((field, key) => renderField({ ...field, key }))}
					</Row>
				</Form>
			</Modal>
		</Fragment>
	);
};

export default MasterMenuPage;
